import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetSubscriptionReportByIdQuery } from '@/store/services/reports';
import { DetailSection } from '../SharedComponents/DetailsSection';
import ReportDrawer from '../SharedComponents/ReportDrawer';
import { usePublicState } from '@/store/slices/publicSlice';

export interface PlanFees {
  price?: number;
  currency?: string;
}

export interface SubscriptionDetailsData {
  planName: string;
  planFees: PlanFees;
  requestDate: string;
  userId: string;
  userName: string;
  name: string;
  period: string;
  startDate: string;
  endDate: string;

  [key: string]: any;
}

interface RequestDetailsDrawerProps {
  listOfIds: string[];
}

export const RequestDetailsDrawer: React.FC<RequestDetailsDrawerProps> = ({
  listOfIds,
}) => {
  const { modalId } = usePublicState();
  const { t } = useTranslation();

  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(String(modalId)));

  const { data: reportDetails, isFetching } = useGetSubscriptionReportByIdQuery(
    modalId as string,
    { skip: !isDrawerOpen }
  );

  const response: SubscriptionDetailsData = reportDetails?.payload || {};

  const requestInfoItems = [
    {
      label: t('reports.planName', 'Plan name'),
      value: response?.planName || '-',
    },
    {
      label: t('reports.planFees', 'Plan fees'),
      value: `${response.planFees?.price} ${response.planFees?.currency}`,
    },
    {
      label: t('reports.requestDate', 'Request date'),
      value: response?.requestDate
        ? dayjs(response.requestDate).isValid()
          ? dayjs(response.requestDate).format('DD MMM YYYY')
          : String(response.requestDate)
        : '-',
    },
    {
      label: t('reports.userId', 'User ID'),
      value: response?.userSerialId || '-',
    },
    {
      label: t('reports.username', 'Username'),
      value: response?.userName || response?.username || '-',
    },
    {
      label: t('reports.name', 'Name'),
      value: response?.name || response?.userName || response?.username || '-',
    },
    {
      label: t('reports.period', 'Period'),
      value: response?.period
        ? response.period.charAt(0).toUpperCase() +
          response.period.slice(1).toLowerCase()
        : '-',
    },
    {
      label: t('reports.startDate', 'Start date'),
      value: response?.startDate
        ? dayjs(response.startDate).isValid()
          ? dayjs(response.startDate).format('DD MMM YYYY')
          : String(response.startDate)
        : '-',
    },
    {
      label: t('reports.endDate', 'End date'),
      value: response?.endDate
        ? dayjs(response.endDate).isValid()
          ? dayjs(response.endDate).format('DD MMM YYYY')
          : String(response.endDate)
        : '-',
    },
  ];

  return (
    <ReportDrawer
      title={t('reports.subscriptionDetails', 'Subscription details')}
      listOfIds={listOfIds}
    >
      <div className="space-y-6">
        {/* Request Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.requestInformation', 'Request information')}
          items={requestInfoItems}
        />
      </div>
    </ReportDrawer>
  );
};

export default RequestDetailsDrawer;
